<template>
	<div class="tree-root">
		<ul class="tree-list">
			<li
			v-for="item in flat"
			:key="item.node.id"
			class="tree-row"
			:data-node-id="item.node.id"
			:class="[
				{ 'is-active-node': selectedNodeId === item.node.id},
				{ 'drop-target-before': props.isDraggingNode && props.dropTargetNodeId === item.node.id && props.dropPosition === 'before' },
				{ 'drop-target-after': props.isDraggingNode && props.dropTargetNodeId === item.node.id && props.dropPosition === 'after' },
				item.node.className || ''
			]"
			:style="{ paddingLeft: (8 + item.level * 16) + 'px' }"
			:draggable="props.canDragNode(item.node)"
			@dragstart="onDragStart($event, item.node)"
			@dragover.prevent="onDragOver($event, item.node)"
			@drop.prevent="onDrop($event, item.node)"
			@contextmenu.prevent="openDropdown($event, item.node)"
			>	
				<span
				class="tree-toggle"
				:class="{ 'is-leaf': !(item.node.children && item.node.children.length)}"
				@click.stop="toggle(item.node)"
				>
					<template v-if="item.node.children && item.node.children.length">
						{{ item.node.expanded ? '−' : '+' }}
					</template>
					<template v-else>&nbsp;</template>					
				</span>		
				<span
				class="tree-title"
				:title="item.node.name"
				@click="select(item.node)"
				@dblclick="dblclick(item.node)"
				:style="{color:item.node.hide ? 'red':''}"
				>
					<template v-if="props.showOrdnung">
						{{ (typeof item.node.ordnung === 'number' ? item.node.ordnung + 1 : item.index + 1) }} {{ item.node.name }}
					</template>
					<template v-else><span v-html="item.node.prefix"></span>{{ item.node.name }}</template>
				</span>
			</li>
		</ul>
		<el-dropdown
		ref="ddRef"
		:disabled="design==3 || !props.groupMenu.length"
		:teleported="false"
		trigger="contextmenu"
		placement="bottom-start"
		:style="{ top: ctxY + 'px', left: ctxX + 'px' }"
		style="position: fixed"
		@command="cmd => emit('fromGroupMenu', { cmd, node: ctxNode })"
		>
			<div style="width:0; height:0">&nbsp;</div>
			<template #dropdown>
				<el-dropdown-menu>
					<el-dropdown-item
					v-for="item in props.groupMenu"
					:key="item.key"
					:command="item"
					>
						{{ item.name }}
					</el-dropdown-item>
				</el-dropdown-menu>
			</template>
		</el-dropdown>
	</div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { ElDropdown, ElDropdownMenu, ElDropdownItem } from 'element-plus'

const props = defineProps({
	teleported: { type: Boolean, default: false },
	design: { type: Number, default: 0 },
	rootNode: { type: Object, required: true },
	selectedNodeId: { type: [Number, null, undefined], default: undefined },
	groupMenu: { type: Array, default: () => [] },
	canDragNode: { type: Function, default: () => () => true },
	onOpenNodeDropdown: { type: Function,  default: () => {}},
	showOrdnung: { type: Boolean, default: false },
	dropTargetNodeId: { type: [Number, null, undefined], default: undefined },
	dropPosition: { type: String, default: '' },
	isDraggingNode: { type: Boolean, default: false },
})

const emit = defineEmits([
	'startDragNode',
	'dropOnNode',
	'dragOverNode',
	'dragLeaveNode',
	'toggleExpand',
	'selectNode',
	'dblclickNode',
	'fromGroupMenu'
])

const flat = computed(() => {
	const out = []
	let index = 0
	function walk(parent, level) {
		for (const child of parent.children || []) {
			out.push({ node: child, level, index: index++ })
			if (child.expanded && child.children?.length) {
				walk(child, level + 1)
			}
		}
	}
	walk(props.rootNode, 0)
	return out
})

function select(node) {
	emit('selectNode', node)
}

function dblclick(node) {
	emit('dblclickNode', node)
}

function toggle(node) {
	if (!(node.children && node.children.length)) return
	emit('toggleExpand', node)
}

function onDragStart(e, node) {	
	if (!props.canDragNode(node) || node.fixed==true) {
		e.preventDefault()
		return
	}
	e.dataTransfer.effectAllowed = 'move'
	emit('startDragNode', node)
}


function onDragOver(e, node) {
	emit('dragOverNode', node, e)
}

function onDrop(e, node) {
	emit('dropOnNode', node)
}

const ddRef = ref(null)
const ctxNode = ref(null)
const ctxX = ref(0)
const ctxY = ref(0)

defineExpose({ddRef})

function openDropdown(e, node) {
	if(props.onOpenNodeDropdown(node)===false) return
	ctxNode.value = node
	ctxX.value = e.clientX 
	ctxY.value = e.clientY 
	if(props.teleported){
		let page = document.querySelector('.pageLayout')?.getBoundingClientRect() 
		if(page){
			ctxX.value -= page.left
			ctxY.value -= page.top
		}
	}
	ddRef.value?.handleClose?.()
	ddRef.value?.handleOpen?.()

}

onMounted(() => {
})

</script>

<style scoped>
.tree-list {
	list-style: none;
	margin: 0;
	padding: 0;
}

.tree-row {
	display: flex;
	align-items: center;
	height: 26px;
	line-height: 26px;
	cursor: pointer;
	user-select: none;
}

.tree-row:hover {
	background-color: var(--el-fill-color-light);
}

.tree-row.is-active-node {
	background-color: #d9ecff;
}

.tree-toggle {
	width: 10px;
	height: 10px;
	margin-right: 3px;
	box-sizing: border-box;
	border: 1px solid #999;
	border-radius: 2px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-weight: normal;
	font-size: 8px;
	line-height: 1;
	background-color: #fff;
}

.tree-toggle.is-leaf {
	border-color: transparent;
	background-color: transparent;
	opacity: 0.4;
}

.tree-title {
	flex: 1;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.tree-row.drop-target-before {
	border-top: 2px dashed var(--el-color-primary);
	margin-top: -1px;
}

.tree-row.drop-target-after {
	border-bottom: 2px dashed var(--el-color-primary);
	margin-bottom: -1px;
}
</style>
